import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "../../Const/Url";
import { logout_func2 } from "../../Const/logoutCommon";
import { showToast } from "../../../utils/ToastHelper";
import Loader from "../../CustomCommons/Loader";
import DeleteIcon from "../../CustomCommons/DeleteIcon";
import EditIcon from "../../CustomCommons/EditIcon";
import plus from "../../../assets/Icon/plus.svg";

const LIMIT = 10;

const BlogCatList = () => {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [offset, setOffset] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit
  const [modalName, setModalName] = useState("");
  const [modalIdentifier, setModalIdentifier] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getOffsetFromUrl = (url) => {
    if (!url) return 0;
    try {
      const parsed = new URL(url);
      return Number(parsed.searchParams.get("offset") || 0);
    } catch (error) {
      const queryString = url?.split("?")[1] || "";
      const params = new URLSearchParams(queryString);
      return Number(params.get("offset") || 0);
    }
  };

  const fetchCategories = useCallback(
    (searchText = "", { urlOverride, offsetValue = 0 } = {}) => {
      setIsLoading(true);
      const resolvedOffset =
        typeof offsetValue === "number" && offsetValue >= 0 ? offsetValue : 0;

      let requestUrl = urlOverride;
      if (!requestUrl) {
        const params = new URLSearchParams();
        params.append("limit", LIMIT);
        params.append("offset", resolvedOffset);
        if (searchText) {
          params.append("search", searchText);
        }
        requestUrl = `${BASE_URL}api/v1/frontend/admin/v2/blog-categories/?${params.toString()}`;
      }

      axios
        .get(requestUrl)
        .then((res) => {
          const payload = res?.data?.data || {};
          setItems(payload?.results || []);
          setTotalData(payload?.count || 0);
          setNextUrl(payload?.next || null);
          setPrevUrl(payload?.previous || null);
          setOffset(resolvedOffset);
          setPageNumber(Math.floor(resolvedOffset / LIMIT) + 1);
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          const status = err?.response?.status;

          if (message === "Invalid token." || status === 401) {
            logout_func2(() => {}, history);
            return;
          }
          showToast("error", message || "Failed to load blog categories");
        })
        .finally(() => setIsLoading(false));
    },
    [history]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCategories(query, { offsetValue: 0 });
    }, 400);

    return () => clearTimeout(debounce);
  }, [query, fetchCategories]);

  const totalPages = LIMIT ? Math.ceil((totalData || 0) / LIMIT) || 1 : 1;

  const handlePaginationPrevious = () => {
    if (!prevUrl) return;
    const derivedOffset = getOffsetFromUrl(prevUrl);
    fetchCategories(query, {
      urlOverride: prevUrl,
      offsetValue: derivedOffset,
    });
  };

  const handlePaginationNext = () => {
    if (!nextUrl) return;
    const derivedOffset = getOffsetFromUrl(nextUrl);
    fetchCategories(query, {
      urlOverride: nextUrl,
      offsetValue: derivedOffset,
    });
  };

  const openCreateModal = () => {
    setModalMode("create");
    setModalName("");
    setModalIdentifier(null);
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    const identifier = item?.slug || item?.id;
    if (!identifier) {
      showToast("error", "Unable to identify blog category");
      return;
    }
    setModalMode("edit");
    setModalName(item?.name || "");
    setModalIdentifier(identifier);
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    const identifier =  item?.slug;
    if (!identifier) {
      showToast("error", "Unable to identify blog category");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this data.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (!willDelete) {
        swal("Your data is safe!");
        return;
      }

      axios
        .delete(
          `${BASE_URL}api/v1/frontend/admin/v2/blog-categories/${identifier}/`
        )
        .then(() => {
          swal("Blog category deleted!", { icon: "success" });
          showToast("success", "Blog category deleted successfully");
          fetchCategories(query, { offsetValue: offset });
        })
        .catch((err) => {
          const message = err?.response?.data?.message;
          showToast("error", message || "Failed to delete blog category");
        });
    });
  };

  const handleModalSubmit = () => {
    if (!modalName?.trim()) {
      showToast("error", "Name is required");
      return;
    }

    setIsSubmitting(true);

    if (modalMode === "create") {
      axios
        .post(`${BASE_URL}api/v1/frontend/admin/v2/blog-categories/`, {
          name: modalName?.trim(),
        })
        .then(() => {
          showToast("success", "Blog category created");
          setIsModalOpen(false);
          fetchCategories(query, { offsetValue: 0 });
        })
        .catch((err) => {
          const message =
            err?.response?.data?.message ||
            err?.response?.data?.errors?.name?.[0];
          showToast("error", message || "Failed to create blog category");
        })
        .finally(() => setIsSubmitting(false));
      return;
    }

    // edit
    axios
      .patch(
        `${BASE_URL}api/v1/frontend/admin/v2/blog-categories/${modalIdentifier}/`,
        { name: modalName?.trim() }
      )
      .then(() => {
        showToast("success", "Blog category updated");
        setIsModalOpen(false);
        fetchCategories(query, { offsetValue: offset });
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.errors?.name?.[0];
        showToast("error", message || "Failed to update blog category");
      })
      .finally(() => setIsSubmitting(false));
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent">
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              padding: "20px",
              width: "360px",
              maxWidth: "90%",
              boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
            }}
          >
            <h5 className="mb-3">
              {modalMode === "create"
                ? "Add Blog Category"
                : "Edit Blog Category"}
            </h5>
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                value={modalName}
                onChange={(e) => setModalName(e.target.value)}
                placeholder="Enter name"
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                className="btn btn-light"
                onClick={() => {
                  if (isSubmitting) return;
                  setIsModalOpen(false);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleModalSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="cotagorey-head mb-4">
        <div>
          <h3>Blog Categories</h3>
          <input
            style={{ color: "black" }}
            type="text"
            className="filter-input"
            placeholder="Search here"
            onChange={(e) => {
              setQuery(e.target?.value);
            }}
          />
        </div>
        <div className="filter_div">
          <div className="button-div" onClick={openCreateModal}>
            <img src={plus} alt="Add blog category" />
            <span>Add Category</span>
          </div>
        </div>
      </div>

      <div style={{ display: "block", overflowX: "auto" }}>
        <table className="table_my pdf-table my-3">
          <thead
            className="bg-dark text-white head-row mt-3 main-head-print"
            style={{ height: "50px" }}
          >
            <tr>
              <th style={{ minWidth: "120px", paddingLeft: "10px" }}>ID</th>
              <th style={{ minWidth: "220px" }}>Name</th>
              <th style={{ minWidth: "180px" }}>Created At</th>
              <th style={{ minWidth: "180px" }}>Updated At</th>
              <th style={{ width: "140px", textAlign: "center" }}>Action</th>
            </tr>
          </thead>
          <tbody className="tb">
            {items?.length ? (
              items.map((item, index) => (
                <tr key={item?.id || index} className="table-row">
                  <td style={{ paddingLeft: "10px" }}>{item?.id ?? "-"}</td>
                  <td>{item?.name || "-"}</td>
                  <td>{formatDate(item?.created_at)}</td>
                  <td>{formatDate(item?.updated_at)}</td>
                  <td className="action">
                    <div className="d-flex justify-content-center gap-3">
                      <div
                        className="edit-del green"
                        onClick={() => openEditModal(item)}
                      >
                        <EditIcon />
                      </div>
                      <div
                        className="edit-del red"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">
                  <div className="not_found d-flex justify-content-center align-items-center py-4">
                    <h4 className="my-4">No Data Found</h4>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalData > 0 && (
        <div className="row pag" style={{ marginTop: "30px" }}>
          <div className="col-12 col-md-4">
            <p className="inter" style={{ color: "#AEAEB2", fontSize: "16px" }}>
              Total Entries:{" "}
              <span style={{ color: "black", fontSize: "14px" }}>
                {totalData}
              </span>
            </p>
          </div>
          {totalPages > 1 && (
            <div className="col-12 col-md-8 d-flex justify-content-md-end">
              <div className="pagination-controls d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationPrevious}
                  disabled={!prevUrl}
                >
                  Prev
                </button>
                <span className="d-flex align-items-center">
                  Page {pageNumber} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={handlePaginationNext}
                  disabled={!nextUrl}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BlogCatList;
